import { NetworkImage, PlaceholderImage, Typography } from "@/components/atoms";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { ImageSourcePropType, Platform, Pressable, StyleSheet, View } from "react-native";
import { PropertyPriceBadge } from "./PropertyPriceBadge";
import { PropertyStatsRow } from "./PropertyStatsRow";

export interface PropertyListingCardProps {
  images?: ImageSourcePropType[];
  unitTitle: string; // e.g., "Flat No A1-401, 3BHK, Bloom Central, Abu Dhabi"
  /** Project description under the title. Hidden when empty. */
  description?: string;
  /** Omit/0 hides the Bedrooms stat instead of showing a placeholder. */
  bedrooms?: number | null;
  /** Omit/0 hides the Bathrooms stat instead of showing a placeholder. */
  bathrooms?: number | null;
  /** Omit/0 hides the Area stat instead of showing a placeholder. */
  areaSqFt?: number | null;
  /** Preformatted area display used by the ADHA listing flow. */
  areaSqFtDisplay?: string | undefined;
  /** Omit/0 hides the price row instead of showing a placeholder. */
  price?: number | null;
  handoverDate: string | null;
  layout?: "grid" | "list";
  onPress?: () => void;
}

/** First image's `uri`, if `images` has one and it's a `{ uri }` source. */
function resolveFirstImageUri(images?: ImageSourcePropType[]): string | undefined {
  const firstImage = images && images.length > 0 ? images[0] : undefined;
  return firstImage && typeof firstImage === "object" && "uri" in firstImage
    ? firstImage.uri
    : undefined;
}

export function PropertyListingCard({
  images,
  unitTitle,
  description,
  bedrooms,
  bathrooms,
  areaSqFt,
  areaSqFtDisplay,
  price,
  handoverDate,
  layout = "grid",
  onPress,
}: Readonly<PropertyListingCardProps>) {
  const c = useUIColors();
  const { isRTL } = useI18nContext();
  // Mobile-only: width:100% gives textAlign room to take effect, and
  // direction:'ltr' makes it deterministic regardless of ambient RTL
  // mirroring. Web keeps its original plain textAlign-only style.
  const titleStyle =
    Platform.OS === "web"
      ? { textAlign: isRTL ? ("right" as const) : ("left" as const) }
      : {
          width: "100%" as const,
          direction: "ltr" as const,
          textAlign: isRTL ? ("right" as const) : ("left" as const),
        };

  const statsRow = (
    <PropertyStatsRow
      bedrooms={bedrooms ?? null}
      bathrooms={bathrooms ?? null}
      areaSqFt={areaSqFt ?? null}
      areaSqFtDisplay={areaSqFtDisplay}
      layout={layout}
    />
  );

  const priceBadge = (
    <PropertyPriceBadge price={price ?? null} handoverDate={handoverDate} />
  );

  const trimmedDescription = description?.trim();
  const descriptionText = trimmedDescription ? (
    <Typography variant="caption" color="secondary" numberOfLines={2} style={titleStyle}>
      {trimmedDescription}
    </Typography>
  ) : null;

  if (layout === "list") {
    const firstImageUri = resolveFirstImageUri(images);

    return (
      <Pressable
        style={[
          styles.listContainer,
          {
            backgroundColor: c.backgroundLevel1,
            borderColor: c.backgroundLevel2,
          },
        ]}
        onPress={onPress}
      >
        <View style={styles.listTopRow}>
          <View style={styles.listImageWrap}>
            {firstImageUri ? (
              <NetworkImage
                url={firstImageUri}
                style={styles.listImage}
                resizeMode="cover"
              />
            ) : (
              <View
                style={[
                  styles.listImagePlaceholder,
                  { backgroundColor: c.backgroundLevel2 },
                ]}
              >
                <PlaceholderImage />
              </View>
            )}
          </View>
          <View style={styles.listContent}>
            <Typography
              variant="headline"
              color="primary"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[titleStyle, styles.listTitleSpacing]}
            >
              {unitTitle}
            </Typography>
            {descriptionText}
            {statsRow}
          </View>
        </View>
        {priceBadge}
      </Pressable>
    );
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: c.backgroundLevel1,
          borderColor: c.backgroundLevel2,
        },
      ]}
    >
      <View style={styles.imageWrap}>
        <ImageCarousel
          images={images ?? []}
          height={200}
          activeDotColor={c.white}
          {...(onPress && { onPress })}
        />
      </View>
      <Pressable onPress={onPress} style={styles.content}>
        <Typography
          variant="headline"
          color="primary"
          numberOfLines={1}
          ellipsizeMode="tail"
          style={titleStyle}
        >
          {unitTitle}
        </Typography>
        {descriptionText}
        {statsRow}
        {priceBadge}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    padding: 8,
    gap: 8,
    borderWidth: 1,
  },
  imageWrap: {
    height: 200,
    borderRadius: 8,
    overflow: "hidden",
  },
  content: {
    gap: 8,
    marginTop: 12,
  },
  listContainer: {
    borderRadius: 12,
    padding: 12,
    gap: 12,
    borderWidth: 1,
  },
  listTopRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  listImageWrap: {
    width: 107,
    height: 75,
    borderRadius: 8,
    overflow: "hidden",
  },
  listImage: {
    width: "100%",
    height: "100%",
  },
  listImagePlaceholder: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  listContent: {
    flex: 1,
    minWidth: 0,
    gap: 8,
    justifyContent: "center",
  },
  // listContent's own `gap: 8` already spaces every child pair — this adds
  // the remainder so title -> next sibling (description, or straight to the
  // bed/bath/sqft stats row when there's no description) totals 14.
  listTitleSpacing: {
    marginBottom: 10,
  },
});

export default PropertyListingCard;
